/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React, { useEffect, useRef, useState } from "react";
import { AppCardComp, Loader } from "../components";
import { VApplication } from "../../../application";
import { PrintLabelPageService } from "../../../services";
import {
  useSMAddSerialNo,
  useSMGetLabelPDF,
  useSMGetPresentationLists,
} from "../machines";
import { StateOSD } from "../../../widgets/state-osd-v2-widget-react-main/src";
import { ColoredBackground } from "../../../common";
import Fireflies from "../../../common/FireFlies";
import { NotificationModal } from "@vlinder-web/notification-modal-v2-widget-react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const is = require("is_js");

const getSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.PrintLabelPageService") as PrintLabelPageService;
};

const SwalWithBootstrapButtons = withReactContent(
  Swal.mixin({
    customClass: {
      confirmButton: "btn btn-info me-3",
      denyButton: "btn btn-secondary",
    },
    buttonsStyling: false,
  })
);

export const ContainerComm = (props: any) => {
  const srv = getSrv();
  const staticPageContentConfig = srv?.getStaticPageContent();
  const [labelFilters, setLabelFilters] = useState({
    presentation: "",
    section: "",
    sectionOptType: "",
  });

  const {
    getPresentationListsMachine,
    getPresentationListsSend,
    getPresentationListsState,
  } = useSMGetPresentationLists({});
  const { getLabelPDFMachine, getLabelPDFSend, getLabelPDFState } =
    useSMGetLabelPDF({});
  const { addSerialNoMachine, addSerialNoSend, addSerialNoState } =
    useSMAddSerialNo({});

  const childRef = useRef(null);

  const handleChildRefClick = () => {
    if (childRef.current) {
      childRef.current.handleClick();
    }
  };

  const _onFetchLabels = (data: any) => {
    setLabelFilters({
      presentation: data?.presentation?.id,
      section: data?.section?.id,
      sectionOptType: data?.sectionOptionType?.id,
    });

    getLabelPDFSend("MUTATE", {
      payload: {
        presentation: data?.presentation?.id,
        section: data?.section?.id,
        sectionOptType: data?.sectionOptionType?.id,
      },
    });
  };

  const _onAddSerialNo = (data: any) => {
    addSerialNoSend("MUTATE", {
      payload: {
        presentation: data?.presentation?.id,
        section: data?.section?.id,
        sectionOptType: data?.sectionOptionType?.id,
      },
    });
  };

  const _onDownloadAnyWay = () => {
    if (
      labelFilters?.presentation &&
      is.existy(labelFilters?.presentation) &&
      labelFilters?.section &&
      is.existy(labelFilters?.section) &&
      labelFilters?.sectionOptType &&
      is.existy(labelFilters?.sectionOptType)
    ) {
      getLabelPDFSend("MUTATE", {
        payload: {
          presentation: labelFilters?.presentation,
          section: labelFilters?.section,
          sectionOptType: labelFilters?.sectionOptType,
          downloadAnyway: true,
        },
      });
    }
  };

  const _onDownloadOnlySerial = () => {
    if (
      labelFilters?.presentation &&
      is.existy(labelFilters?.presentation) &&
      labelFilters?.section &&
      is.existy(labelFilters?.section) &&
      labelFilters?.sectionOptType &&
      is.existy(labelFilters?.sectionOptType)
    ) {
      getLabelPDFSend("MUTATE", {
        payload: {
          presentation: labelFilters?.presentation,
          section: labelFilters?.section,
          sectionOptType: labelFilters?.sectionOptType,
          downloadPartial: true,
          downloadAnyway: true,
        },
      });
    }
  };

  const _onViewAndDownloadLabelPDF = (payload: any) => {
    if (payload?.pdf && is.existy(payload?.pdf)) {
      const fileName = `${payload?.presentation} - ${payload?.section} - ${payload?.sectionOptType} labels`;
      const anchor = document.createElement("a");
      anchor.href = payload?.pdf;
      anchor.target = "_blank";
      anchor.download = `${fileName}.pdf`;

      anchor.click();
    }
  };

  const _renderAppCard = () => {
    if (getPresentationListsState?.matches("GET_PRESENTATION_LISTS")) {
      return (
        <StateOSD
          {...(staticPageContentConfig?.loading?.getPresentationListsLoading ||
            {})}
        />
      );
    }

    if (
      getPresentationListsState?.matches("GET_PRESENTATION_LISTS_SUCCESS") &&
      getPresentationListsState?.event?.data?.value
    ) {
      if (
        getPresentationListsState?.event?.data?.value &&
        is.not.empty(getPresentationListsState?.event?.data?.value?.content)
      ) {
        return (
          <AppCardComp
            presentationList={
              getPresentationListsState?.event?.data?.value?.content
            }
            parentDownloadRef={childRef}
            onFetchLabels={_onFetchLabels}
            onAddSerialNo={_onAddSerialNo}
          />
        );
      }

      return (
        <StateOSD
          {...(staticPageContentConfig?.loading?.getPresentationListsEmpty ||
            {})}
        />
      );
    }

    if (
      getPresentationListsState?.matches("GET_PRESENTATION_LISTS_FAILED") &&
      getPresentationListsState?.event?.data?.value
    ) {
      return (
        <StateOSD
          {...(staticPageContentConfig?.error?.getPresentationListsError || {})}
          description={getPresentationListsState?.event?.data?.value}
          onButtonClick={() => {
            getPresentationListsSend("START");
          }}
        />
      );
    }
  };

  useEffect(() => {
    const triggerFunction = async () => {
      if (
        getLabelPDFState?.matches("GET_LABEL_PDF_SUCCESS") &&
        is.existy(getLabelPDFState?.event?.data?.value?.message) &&
        getLabelPDFState?.event?.data?.value?.message === "Partial"
      ) {
        const title = `<h4>Confirmation</h4>`;
        const description = `<span style="font-size:16px">Few Costumes doesn't have serial number.</span>`;

        const result = await SwalWithBootstrapButtons.fire({
          icon: "info",
          title: title,
          html: description,
          showCloseButton: true,
          showDenyButton: true,
          focusConfirm: false,
          confirmButtonText: "Download Labels with Serial No.",
          denyButtonText: "Download All Labels",
        }).then((result) => {
          if (result.isConfirmed) {
            _onDownloadOnlySerial();
          } else if (result.isDenied) {
            _onDownloadAnyWay();
          }
        });
      }
    };

    triggerFunction();
  }, [getLabelPDFState?.matches("GET_LABEL_PDF_SUCCESS")]);

  const _renderLabelPdf = () => {
    if (getLabelPDFState?.matches("GET_LABEL_PDF")) {
      return <Loader />;
    }

    if (
      getLabelPDFState?.matches("GET_LABEL_PDF_SUCCESS") &&
      getLabelPDFState?.event?.data?.value
    ) {
      const payload = getLabelPDFState?.event?.data?.value;

      if (payload?.message === "No") {
        return (
          <NotificationModal
            showModal={true}
            {...(staticPageContentConfig?.notifications
              ?.getLabelPDFDownloadAnyWay || {})}
            onAcknowledgement={() => _onDownloadAnyWay()}
            onDeny={() => {}}
          />
        );
      } else if (payload?.message === "Partial") {
        return <></>;
      }

      return (
        <NotificationModal
          showModal={true}
          {...(staticPageContentConfig?.notifications?.getLabelPDFSuccess ||
            {})}
          onAcknowledgement={() =>
            _onViewAndDownloadLabelPDF(getLabelPDFState?.event?.data?.value)
          }
          onDeny={() => {}}
        />
      );
    }

    if (
      getLabelPDFState?.matches("GET_LABEL_PDF_FAILED") &&
      getLabelPDFState?.event?.data?.value
    ) {
      return (
        <NotificationModal
          showModal={true}
          {...(staticPageContentConfig?.error?.getLabelPDFError || {})}
          description={[getLabelPDFState?.event?.data?.value]}
          onAcknowledgement={() => {}}
          onDeny={() => {}}
        />
      );
    }
  };

  useEffect(() => {
    const triggerFunction = async () => {
      if (
        addSerialNoState?.matches("ADD_SERIAL_NO_SUCCESS") &&
        is.existy(addSerialNoState?.event?.data?.value?.status) &&
        addSerialNoState?.event?.data?.value?.status === "PARTIAL"
      ) {
        const title = `<h4>Serial numbers added successfully</h4>`;
        const description = `<span style="font-size:16px">Serial numbers assigned to these new costumes.</span>`;

        const result = await SwalWithBootstrapButtons.fire({
          icon: "info",
          title: title,
          html: description,
          showCloseButton: true,
          showDenyButton: true,
          focusConfirm: false,
          confirmButtonText: "Download new labels",
          denyButtonText: "Download all labels",
        }).then((result) => {
          if (result.isConfirmed) {
            _onViewAndDownloadLabelPDF(addSerialNoState?.event?.data?.value);
          } else if (result.isDenied) {
            handleChildRefClick();
          }
        });
      }
    };

    triggerFunction();
  }, [addSerialNoState?.matches("ADD_SERIAL_NO_SUCCESS")]);

  const _renderAddingSerialNumber = () => {
    if (addSerialNoState?.matches("ADD_SERIAL_NO")) {
      return <Loader />;
    }

    if (
      addSerialNoState?.matches("ADD_SERIAL_NO_SUCCESS") &&
      is.existy(addSerialNoState?.event?.data?.value?.status) &&
      addSerialNoState?.event?.data?.value?.status === "FULL"
    ) {
      return (
        <NotificationModal
          showModal={true}
          {...(staticPageContentConfig?.notifications?.addSerialNoSuccess ||
            {})}
          onAcknowledgement={() =>
            _onViewAndDownloadLabelPDF(addSerialNoState?.event?.data?.value)
          }
          onDeny={() => {}}
        />
      );
    }

    if (
      addSerialNoState?.matches("ADD_SERIAL_NO_FAILED") &&
      addSerialNoState?.event?.data?.value
    ) {
      return (
        <NotificationModal
          showModal={true}
          {...(staticPageContentConfig?.error?.addSerialNoError || {})}
          description={[addSerialNoState?.event?.data?.value]}
          onAcknowledgement={() => {}}
          onDeny={() => {}}
        />
      );
    }
  };

  return (
    <>
      <ColoredBackground />
      <Fireflies settings={{ count: 100, size: 3, speed: 1.1 }} />
      {_renderAppCard()}
      {_renderLabelPdf()}
      {_renderAddingSerialNumber()}
    </>
  );
};
